import type { RunSummary, RunResultKind } from '@app/shared';
import { StatusBadge, Icons, type LucideIcon } from '../ui';
import { RunStatusBadge } from './RunStatusBadge';

/**
 * The runs-list OUTCOME badge (§14, #263). Distinguishes runs that PRODUCED A RESULT from
 * runs that ended with no result, layered on top of the raw status chip:
 *
 *   - `result`    → a brand/positive pill NAMING the output kind ("Draft"/"Sent"/"Report"/
 *                   "Event"/"File"/"Action"), so an operator sees at a glance what the run
 *                   made — an inbox draft, a research report, a sent reply, a calendar event.
 *   - `no_result` → a muted "No action" pill, with the run's `endedReason` as a tooltip
 *                   (the "no action needed"/filtered/marketing path).
 *   - `failed`    → the existing red status chip (Failed/Cancelled/Blocked/Limit exceeded).
 *   - `active`    → the existing status chip (Running/Queued/Waiting/Awaiting approval) — a
 *                   live run is never masked by an outcome verdict.
 *
 * Colour is never the only cue: each result kind carries a distinct icon + text label.
 */
const RESULT_KIND_ICON: Record<RunResultKind, LucideIcon> = {
  draft: Icons.PenLine,
  sent: Icons.Send,
  event: Icons.CalendarPlus,
  file: Icons.FileUp,
  report: Icons.FileText,
  action: Icons.Sparkles,
};

export interface RunOutcomeBadgeProps {
  run: Pick<RunSummary, 'status' | 'outcome' | 'resultKind' | 'resultLabel' | 'endedReason'>;
  size?: 'sm' | 'md';
}

/**
 * Renders the right badge for a run's derived outcome. `failed`/`active` delegate to the
 * existing `RunStatusBadge` (preserving the `status-chip` testid); `result`/`no_result`
 * render the dedicated outcome pill (`run-outcome-badge` testid, `data-outcome` attr).
 */
export function RunOutcomeBadge({ run, size = 'sm' }: RunOutcomeBadgeProps) {
  if (run.outcome === 'result') {
    const kind = run.resultKind ?? 'action';
    return (
      <StatusBadge
        tone="brand"
        icon={RESULT_KIND_ICON[kind]}
        label={run.resultLabel ?? 'Result'}
        size={size}
        data-testid="run-outcome-badge"
        data-outcome="result"
        data-result-kind={kind}
      />
    );
  }
  if (run.outcome === 'no_result') {
    return (
      <StatusBadge
        tone="neutral"
        icon={Icons.MinusCircle}
        label="No action"
        size={size}
        // Surface WHY nothing happened on hover (e.g. "no action needed" / a filter reason).
        title={run.endedReason ?? 'No action needed'}
        data-testid="run-outcome-badge"
        data-outcome="no_result"
      />
    );
  }
  // failed / active: the raw status chip is the most informative here.
  return <RunStatusBadge status={run.status} size={size} />;
}
